import axios, {CanceledError} from 'axios';

import {type ServerError} from '@mattermost/types/errors';
import {type FileInfo, type FileUploadResponse} from '@mattermost/types/files';
import {FileTypes} from 'mattermost-redux/action_types';
import {Client4} from 'mattermost-redux/client';
import {type DispatchFunc} from 'mattermost-redux/types/actions';

import * as Utils from 'utils/utils';

type Props = {
    file: File;
    channelId: string;
    rootId?: string;
    clientId: string;
    onProgress: (percent: number) => void;
    onError: (error: string | ServerError) => void;
    onSuccess: (data: FileUploadResponse) => void;
}

export function uploadFile({file, channelId, rootId, clientId, onProgress, onError, onSuccess}: Props) {
    return (dispatch: DispatchFunc) => {
        dispatch({type: FileTypes.UPLOAD_FILES_REQUEST});
        const formData = new FormData();

        // The order here is important:
        // keeping the channel_id/client_ids fields before the files contents
        // allows the server to stream the uploads instead of loading them in memory.
        formData.append('channel_id', channelId);
        formData.append('client_ids', clientId);
        formData.append('files', file, file.name);

        const abortController = new AbortController();
        axios.post(Client4.getFilesRoute(), formData, {
            headers: {
                ...Client4.getOptions({method: 'post'}).headers,
                Accept: 'application/json',
            },
            onUploadProgress: (progressEvent) => {
                if (!progressEvent.total) {
                    return;
                }

                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(progress);
            },
            signal: abortController.signal,
        })
            .then((response) => {
                const res: FileUploadResponse = response.data;
                const data = res.file_infos.map((fileInfo: FileInfo, index: number) => {
                    return {
                        ...fileInfo,
                        clientId: res.client_ids[index],
                    };
                });

                dispatch({
                    type: FileTypes.RECEIVED_UPLOAD_FILES,
                    data,
                    channelId,
                    rootId,
                });
                dispatch({type: FileTypes.UPLOAD_FILES_SUCCESS});

                onSuccess(res);
            })
            .catch((error) => {
                let errorMessage;
                if (error.response && error.response.data) {
                    errorMessage = error.response.data;
                } else if (error.request) {
                    if (!(error instanceof CanceledError)) {
                        errorMessage = {
                            message: Utils.localizeMessage(
                                'file_upload.generic_error',
                                'There was a problem uploading your files.',
                            ),
                        };
                    }
                } else {
                    errorMessage = {
                        message:
                            Utils.localizeMessage(
                                'channel_loader.unknown_error',
                                'We received an unexpected status code from the server.',
                            ) +
                            ' (' +
                            error.response.status +
                            ')',
                    };
                }

                dispatch({
                    type: FileTypes.UPLOAD_FILES_FAILURE,
                    clientIds: [clientId],
                    channelId,
                    rootId,
                    error: errorMessage,
                });

                onError(errorMessage);
            });

        return abortController;
    };
}
